import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from '@/hooks/use-toast';
import { Lock, CheckCircle2 } from 'lucide-react';
import slicelogo from '@/assets/slice-logo-v2.png';
import { mapSupabaseError } from '@/utils/security';

const passwordSchema = z.object({
  password: z.string().min(8, 'Password must be at least 8 characters').max(100),
  confirmPassword: z.string(),
}).refine(data => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type PasswordFormValues = z.infer<typeof passwordSchema>;

export default function SetupPasswordPage() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const form = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('SetupPasswordPage auth event:', event, session ? 'session exists' : 'no session');
      if (event === 'SIGNED_IN' && session) {
        setUserEmail(session.user.email || null);
        setLoading(false);
      } else if (event === 'TOKEN_REFRESHED' && session) {
        setUserEmail(session.user.email || null);
        setLoading(false);
      } else if (event === 'INITIAL_SESSION') {
        if (session) {
          setUserEmail(session.user.email || null);
          setLoading(false);
        }
        // If no session on INITIAL_SESSION, wait for token exchange from hash
      }
    });

    // Fallback timeout - if no session after 10 seconds, redirect
    const timeout = setTimeout(async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.log('SetupPasswordPage: No session after timeout, redirecting to auth');
        navigate('/auth', { replace: true });
      }
    }, 10000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, [navigate]);

  const onSubmit = async (values: PasswordFormValues) => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: values.password,
      });

      if (error) throw error;

      // Check if profile already has account_type and setup_completed (imported user)
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('account_type, setup_completed, beta_access')
          .eq('user_id', user.id)
          .single();

        if (profile?.account_type && profile?.setup_completed) {
          // Imported user with full setup — go straight to dashboard
          // Mark account_imports as claimed via secure RPC
          await supabase.rpc('claim_account_import' as any);
          toast({
            title: 'Password Set Successfully! 🎉',
            description: 'Welcome to TheSlice! Redirecting to your dashboard.',
          });
          // Clear any signup verification flags
          sessionStorage.removeItem('isSignupVerification');
          navigate('/', { replace: true });
          return;
        }

        if (profile?.account_type && !profile?.setup_completed) {
          // Has account type but needs onboarding
          toast({
            title: 'Password Set Successfully! 🎉',
            description: 'Now let\'s complete your profile setup.',
          });
          navigate(`/onboarding?type=${profile.account_type}`, { replace: true });
          return;
        }
      }

      toast({
        title: 'Password Set Successfully! 🎉',
        description: 'Your password has been created. Now let\'s set up your profile.',
      });

      // No account type set — go to role selection
      navigate('/role-selection', { replace: true });
    } catch (error: any) {
      toast({
        title: 'Setup Failed',
        description: mapSupabaseError(error, 'Failed to set password. Please try again.'),
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-accent/5 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-muted-foreground">Verifying your invitation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-accent/5 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <img src={slicelogo} alt="TheSlice Logo" className="h-16 w-auto mx-auto mb-6 mix-blend-multiply" />
          <h1 className="text-3xl font-bold">Welcome to TheSlice! 🎉</h1>
          <p className="text-muted-foreground mt-2">
            Let's set up your password to secure your account
          </p>
        </div>

        <Card className="border-2">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Lock className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle>Create Your Password</CardTitle>
                <CardDescription className="mt-1">
                  {userEmail && `Setting up password for ${userEmail}`}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Password *</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password (min 8 characters)"
                  {...form.register('password')}
                />
                {form.formState.errors.password && (
                  <p className="text-sm text-destructive">{form.formState.errors.password.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password *</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Re-enter your password"
                  {...form.register('confirmPassword')}
                />
                {form.formState.errors.confirmPassword && (
                  <p className="text-sm text-destructive">{form.formState.errors.confirmPassword.message}</p>
                )}
              </div>

              <div className="bg-muted/50 p-4 rounded-lg space-y-2">
                <p className="text-sm font-medium">Password Requirements:</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary" />
                    At least 8 characters long
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary" />
                    Use a unique password
                  </li>
                </ul>
              </div>

              <Button 
                type="submit" 
                className="w-full" 
                size="lg"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Setting up...' : 'Continue to Profile Setup'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="text-center text-sm text-muted-foreground">
          <p>
            After setting your password, you'll select your account type and complete your profile.
          </p>
        </div>
      </div>
    </div>
  );
}
